/**
 * ThemesAM.jsx — Livret de révision « 12 thèmes du Permis AM »
 *
 * Galerie mémo transversale des thèmes du programme AM.
 * Chaque thème est cliquable pour un focus plein écran (modale).
 * Accessible depuis le Hub Voiturette et le Hub Cyclomoteur.
 *
 * Routing :
 *   - /permis-am/themes                → grille des 12 thèmes
 *   - /permis-am/themes?theme=slug     → ouvre directement le focus d\'un thème
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Home, ArrowRight, X, BookOpen, Route, ShieldCheck,
  Sparkles, Download, ImageIcon, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import useSeo from '../../hooks/useSeo';
import { AM_THEMES, VISIBLE_AM_THEMES, AM_CATEGORIES } from '../../data/cours/permisAmThemes';
import { THEMES_QCM, isQCMValidated as isQcmValidated, countCorrectAnswers } from '../../data/cours/permisAmThemes/qcm';
import { VISUAL_CONFIGS } from '../../data/cours/permisAmThemes/visualConfigs';
import { ProjectorToggle } from '../../components/ProjectorToggle';
import { VisuelAllureAM } from '../../components/permis-am/VisuelAllureAM';
import { VisuelReglesCirculationAM } from '../../components/permis-am/VisuelReglesCirculationAM';
import { VisuelThemeCard } from '../../components/permis-am/VisuelThemeCard';

const ICONS = { BookOpen, Route, ShieldCheck };

export default function ThemesAM() {
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState('all');

  const activeSlug = params.get('theme');
  const activeTheme = VISIBLE_AM_THEMES.find((t) => t.slug === activeSlug) || null;
  const activeIdx = activeTheme ? VISIBLE_AM_THEMES.findIndex((t) => t.slug === activeSlug) : -1;

  useSeo({
    title: activeTheme
      ? `${activeTheme.title} — Thèmes Permis AM · Code 2 Roues`
      : `${VISIBLE_AM_THEMES.length} Thèmes du Permis AM — Livret de révision · Code 2 Roues`,
    description: activeTheme
      ? activeTheme.subtitle
      : 'Livret visuel de révision du Permis AM : signalisation, priorités, distances, allure, virages, partage de la route, angles morts, entretien, risques.',
  });

  const filtered = useMemo(() => {
    if (filter === 'all') return VISIBLE_AM_THEMES;
    return VISIBLE_AM_THEMES.filter((t) => t.category === filter);
  }, [filter]);

  const openTheme = (slug) => {
    const next = new URLSearchParams(params);
    next.set('theme', slug);
    setParams(next, { replace: false });
  };

  const closeTheme = () => {
    const next = new URLSearchParams(params);
    next.delete('theme');
    setParams(next, { replace: false });
  };

  const goto = (delta) => {
    if (activeIdx === -1) return;
    const nextIdx = (activeIdx + delta + VISIBLE_AM_THEMES.length) % VISIBLE_AM_THEMES.length;
    openTheme(VISIBLE_AM_THEMES[nextIdx].slug);
  };

  // Fermeture avec Echap + navigation avec flèches
  useEffect(() => {
    if (!activeTheme) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeTheme();
      if (e.key === 'ArrowRight') goto(1);
      if (e.key === 'ArrowLeft') goto(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [activeTheme, activeIdx]);

  return (
    <div className="min-h-screen bg-background text-foreground pb-16" data-testid="themes-am">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-2 sm:gap-3">
          <Button asChild variant="outline" size="sm" data-testid="btn-back-am-hub">
            <Link to="/permis-am" aria-label="Retour au Hub Permis AM">
              <ArrowLeft className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Permis AM</span>
            </Link>
          </Button>
          <div className="flex-1 text-center min-w-0">
            <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-red-500 truncate whitespace-nowrap">
              {VISIBLE_AM_THEMES.length} Thèmes du Permis AM
            </div>
          </div>
          <ProjectorToggle testid="proj-themes-am" />
          <Button asChild variant="ghost" size="sm">
            <Link to="/" aria-label="Retour à l'accueil">
              <Home className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Accueil</span>
            </Link>
          </Button>
        </div>
      </div>

      <article className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Hero */}
        <header className="text-center mb-10 lg:mb-14">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-[10px] font-black uppercase tracking-widest text-red-400 mb-4">
            <BookOpen className="w-3 h-3" /> Livret de révision
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.05] tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {VISIBLE_AM_THEMES.length} Thèmes du Permis AM
          </h1>
          <p className="text-base lg:text-lg text-white/70 max-w-3xl mx-auto leading-relaxed">
            Un mémo visuel qui couvre tout le programme officiel : signalisation, priorités, distances, virages, partage de la route, angles morts, météo, entretien et risques. À projeter en classe, à consulter avant l'examen.
          </p>
        </header>

        {/* Filtres par catégorie */}
        <div className="mb-8 flex flex-wrap justify-center gap-2" role="tablist" aria-label="Filtrer par catégorie">
          <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} label="Tous les thèmes" count={VISIBLE_AM_THEMES.length} testid="filter-all" />
          {AM_CATEGORIES.map((c) => {
            const count = VISIBLE_AM_THEMES.filter((t) => t.category === c.key).length;
            const Icon = ICONS[c.icon] || BookOpen;
            return (
              <FilterChip
                key={c.key}
                active={filter === c.key}
                onClick={() => setFilter(c.key)}
                label={c.label}
                icon={<Icon className="w-3.5 h-3.5" />}
                color={c.color}
                count={count}
                testid={`filter-${c.key}`}
              />
            );
          })}
        </div>

        {/* Grille des visuels */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
          {filtered.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => openTheme(t.slug)}
              className="group text-left rounded-2xl border-2 border-white/10 bg-[#141416] hover:border-red-500/50 transition overflow-hidden flex flex-col"
              data-testid={`theme-card-${t.slug}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-white">
                {renderThemeVisual(t, 'thumb')}
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9.5px] font-black uppercase tracking-widest" style={{ background: `${t.color}dd`, color: 'white' }}>
                  N°{t.id}
                </div>
                {qcmValidated(t) && (
                  <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9.5px] font-black uppercase tracking-widest bg-emerald-500/95 text-white shadow" data-testid={`theme-validated-${t.slug}`}>
                    ✓ Validé
                  </div>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: t.color }}>
                  Thème {t.id}
                </div>
                <h3 className="mt-1 text-base font-black leading-tight text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {t.title}
                </h3>
                <p className="mt-1 text-[12.5px] text-white/60 flex-1">{t.subtitle}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[10.5px] font-black uppercase tracking-widest text-red-400 group-hover:text-red-300">
                  Ouvrir la fiche <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            </button>
          ))}
        </section>

        {/* CTA vers les hubs */}
        <section className="mt-12 grid sm:grid-cols-2 gap-4">
          <Link
            to="/permis-am/voiturette"
            className="group p-5 rounded-2xl border-2 border-white/10 bg-black/20 hover:border-red-500/40 transition"
            data-testid="cta-hub-voiturette"
          >
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-1">Formation officielle</div>
            <div className="text-lg font-black text-white group-hover:text-red-300" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Parcours pédagogique Voiturette
            </div>
            <p className="text-[13px] text-white/60 mt-1">5 séquences officielles avec référentiel officiel + enrichi Code 2 Roues.</p>
          </Link>
          <Link
            to="/permis-am"
            className="group p-5 rounded-2xl border-2 border-white/10 bg-black/20 hover:border-red-500/40 transition"
            data-testid="cta-hub-am"
          >
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-1">Hub général</div>
            <div className="text-lg font-black text-white group-hover:text-red-300" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Retour au Permis AM
            </div>
            <p className="text-[13px] text-white/60 mt-1">Cyclomoteur, voiturette, cérémonie, programme officiel.</p>
          </Link>
        </section>
      </article>

      {/* Modale focus */}
      {activeTheme && (
        <ThemeFocus
          theme={activeTheme}
          onClose={closeTheme}
          onPrev={() => goto(-1)}
          onNext={() => goto(1)}
          index={activeIdx}
          total={VISIBLE_AM_THEMES.length}
        />
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, icon, color, count, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition border ${
        active
          ? 'bg-red-600 text-white border-red-500'
          : 'bg-white/[0.05] text-white/70 border-white/10 hover:bg-white/[0.08] hover:text-white'
      }`}
      style={active && color ? { background: color, borderColor: color } : undefined}
      data-testid={testid}
      role="tab"
      aria-selected={active}
    >
      {icon}
      <span>{label}</span>
      <span className={`ml-1 px-1.5 rounded-full text-[9.5px] ${active ? 'bg-white/25' : 'bg-white/10'}`}>{count}</span>
    </button>
  );
}

function ThemeFocus({ theme, onClose, onPrev, onNext, index, total }) {
  const onValidated = () => {}; // hook potentiel pour futur suivi enseignant
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="theme-focus-title"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col"
      data-testid="theme-focus"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[13px] font-black" style={{ background: theme.color }}>
            {theme.id}
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: theme.color }}>
              Thème {theme.id} / {total}
            </div>
            <h2 id="theme-focus-title" className="text-sm sm:text-base font-black text-white leading-none" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {theme.title}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onPrev} aria-label="Thème précédent" data-testid="theme-focus-prev">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onNext} aria-label="Thème suivant" data-testid="theme-focus-next">
            <ChevronRight className="w-5 h-5" />
          </Button>
          {theme.src && (
            <Button variant="outline" size="sm" asChild data-testid="theme-focus-download">
              <a href={theme.src} download aria-label="Télécharger le visuel">
                <Download className="w-4 h-4 mr-1" /> Télécharger
              </a>
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Fermer" data-testid="theme-focus-close">
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Corps */}
      <div className="flex-1 overflow-auto p-4 sm:p-8 flex items-center justify-center">
        <div className="max-w-4xl w-full">
          <figure className="rounded-2xl overflow-hidden border-2 bg-white" style={{ borderColor: `${theme.color}55` }}>
            {renderThemeVisual(theme, 'focus')}
          </figure>

          <div className="mt-4 p-4 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-600/10 via-amber-500/5 to-transparent flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-1">À retenir</div>
              <p className="text-sm sm:text-base font-black leading-snug text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {theme.retenir}
              </p>
            </div>
          </div>

          <p className="mt-4 text-[12.5px] text-white/60 italic flex items-start gap-2">
            <ImageIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: theme.color }} />
            <span>{theme.caption}</span>
          </p>

          {/* QCM interactif — validation obligatoire pour passer au thème suivant */}
          <QcmBlock theme={theme} onValidated={onValidated} onNext={onNext} />
        </div>
      </div>
    </div>
  );
}

/* ────────────────── QCM par thème + validation localStorage ────────────────── */

const QCM_STORAGE_KEY = 'c2r:themesAm:qcm';

function loadQcmStore() {
  try { return JSON.parse(localStorage.getItem(QCM_STORAGE_KEY) || '{}'); } catch { return {}; }
}
function saveQcmStore(next) {
  try { localStorage.setItem(QCM_STORAGE_KEY, JSON.stringify(next)); } catch {}
}

function isThemeValidated(slug) {
  const store = loadQcmStore();
  const entry = store[slug];
  if (!entry) return false;
  return isQcmValidated(slug, entry.answers);
}

function qcmValidated(theme) {
  return isThemeValidated(theme.slug);
}

/** Rendu du visuel : composant custom, config générique ou fallback image PNG. */
function renderThemeVisual(theme, variant) {
  if (theme.customRender === 'allure-am') return <VisuelAllureAM variant={variant} />;
  if (theme.customRender === 'regles-circulation-am') return <VisuelReglesCirculationAM variant={variant} />;
  const config = VISUAL_CONFIGS[theme.slug];
  if (config) return <VisuelThemeCard config={config} variant={variant} color={theme.color} />;
  return (
    <img
      src={theme.src}
      alt={`${theme.title} — ${theme.subtitle}`}
      className="w-full h-full object-cover"
      loading="lazy"
    />
  );
}

function QcmBlock({ theme, onValidated, onNext }) {
  const qcm = THEMES_QCM[theme.slug];
  const [store, setStore] = React.useState(() => loadQcmStore());
  const entry = store[theme.slug] || { answers: {}, validated: false };
  const [showCorrection, setShowCorrection] = React.useState(false);

  if (!qcm) {
    return (
      <div className="mt-6 p-4 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-white/60" data-testid={`qcm-empty-${theme.slug}`}>
        📝 QCM à venir pour ce thème.
      </div>
    );
  }

  const validated = isQcmValidated(theme.slug, entry.answers);
  const score = countCorrectAnswers(theme.slug, entry.answers); // { correct, total }
  const total = qcm.questions.length;

  const select = (qIdx, letter) => {
    const nextAnswers = { ...entry.answers, [qIdx]: letter };
    const nextStore = { ...store, [theme.slug]: { answers: nextAnswers, validated: false } };
    setStore(nextStore);
    saveQcmStore(nextStore);
    setShowCorrection(false);
  };

  const submit = () => {
    setShowCorrection(true);
    if (isQcmValidated(theme.slug, entry.answers)) {
      const nextStore = { ...store, [theme.slug]: { ...entry, validated: true, validatedAt: new Date().toISOString() } };
      setStore(nextStore);
      saveQcmStore(nextStore);
      if (onValidated) onValidated(theme.slug);
    }
  };

  const reset = () => {
    const nextStore = { ...store, [theme.slug]: { answers: {}, validated: false } };
    setStore(nextStore);
    saveQcmStore(nextStore);
    setShowCorrection(false);
  };

  const answered = Object.keys(entry.answers).length;
  const canSubmit = answered === total;

  return (
    <div className="mt-8 rounded-2xl border-2 p-5" style={{ borderColor: `${theme.color}55`, background: `${theme.color}0d` }} data-testid={`qcm-${theme.slug}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-black text-sm" style={{ background: theme.color }}>?</div>
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color: theme.color }}>QCM · Je vérifie mes acquis</div>
          <div className="text-white font-black text-lg leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {total} questions — passe au thème suivant après validation
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {qcm.questions.map((q, qIdx) => {
          const userAnswer = entry.answers[qIdx];
          return (
            <div key={qIdx} className="rounded-xl bg-white/[0.04] border border-white/10 p-3">
              <div className="text-white font-black text-[14px] mb-2 flex gap-2">
                <span className="text-white/50">{qIdx + 1}.</span>
                <span>{q.q}</span>
              </div>
              <div className="space-y-1.5">
                {q.choices.map((choice, cIdx) => {
                  const letter = choice.key || String.fromCharCode(65 + cIdx);
                  const label = choice.label || choice;
                  const selected = userAnswer === letter;
                  const isRight = letter === q.answer;
                  const showAsRight = showCorrection && isRight;
                  const showAsWrong = showCorrection && selected && !isRight;
                  return (
                    <button
                      key={letter}
                      type="button"
                      onClick={() => select(qIdx, letter)}
                      disabled={showCorrection}
                      className={`w-full text-left px-3 py-2 rounded-lg border-2 flex items-start gap-2 transition text-[13px] ${
                        showAsRight
                          ? 'border-emerald-500 bg-emerald-500/20 text-emerald-100'
                          : showAsWrong
                          ? 'border-rose-500 bg-rose-500/20 text-rose-100'
                          : selected
                          ? 'border-white/50 bg-white/10 text-white'
                          : 'border-white/10 bg-transparent text-white/80 hover:border-white/30 hover:bg-white/[0.04]'
                      }`}
                      data-testid={`qcm-${theme.slug}-q${qIdx}-${letter}`}
                      aria-pressed={selected}
                    >
                      <span className="font-black shrink-0">{letter}.</span>
                      <span className="flex-1">{label}</span>
                      {showAsRight && <span aria-hidden>✓</span>}
                      {showAsWrong && <span aria-hidden>✗</span>}
                    </button>
                  );
                })}
              </div>
              {showCorrection && q.explain && (
                <div className="mt-2 text-[12px] text-white/70 italic pl-1 border-l-2 pl-3" style={{ borderColor: theme.color }}>
                  💡 {q.explain}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions */}
      {!showCorrection && (
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="mt-4 w-full sm:w-auto inline-flex items-center gap-2 h-11 px-5 rounded-xl text-white text-[12px] font-black uppercase tracking-widest transition disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: canSubmit ? theme.color : '#334155' }}
          data-testid={`qcm-submit-${theme.slug}`}
        >
          Valider mes réponses ({answered}/{total})
        </button>
      )}

      {showCorrection && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className={`px-3 py-1.5 rounded-full text-[12px] font-black uppercase tracking-widest ${
            validated ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
          }`}>
            Score : {score.correct}/{score.total} {validated ? '🎉' : '— à retenter'}
          </div>
          {validated ? (
            <button
              type="button"
              onClick={onNext}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-[11.5px] font-black uppercase tracking-widest transition"
              data-testid={`qcm-next-${theme.slug}`}
            >
              Thème suivant <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-white/10 hover:bg-white/20 text-white text-[11.5px] font-black uppercase tracking-widest transition"
              data-testid={`qcm-retry-${theme.slug}`}
            >
              Recommencer
            </button>
          )}
        </div>
      )}
    </div>
  );
}
